/*
 * Variant picker block behaviour
 * ==============================
 * Moved out of blocks/product-variant-picker-block.liquid so the block's Liquid
 * stays small and this file is cached by the browser. Two parts:
 *
 *  1. Pre-selection + "Colour: Red" label sync (always).
 *  2. Custom media-gallery filter (only when a picker on the page carries
 *     data-gallery-filter-config, i.e. the block's shared-media settings are on).
 *
 * Both parts run after the document is parsed and after main.js (a deferred
 * module loaded in <head>), which is the order the inline versions relied on.
 * onReady() runs its callback on DOMContentLoaded when that is still pending
 * (this file is loaded with `defer`, so that is the normal case) and otherwise
 * on the next tick, e.g. when the theme editor injects a section later.
 */
(function () {
  'use strict';

  function onReady(fn) {
    var done = false;
    function once() { if (done) return; done = true; fn(); }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', once);
      return;
    }
    document.addEventListener('DOMContentLoaded', once);
    setTimeout(once, 0);
  }

  /* ---- 1. Pre-selection and selected-value labels ---------------------- */
  (function () {
    if (window.__vpSelectedValueLabels) return;
    window.__vpSelectedValueLabels = true;
  
    /*
      Make sure the pre-selected value is actually checked on load.
  
      The server writes `checked` onto the matching radio, but that can be
      lost before the customer ever sees it: two copies of this block in one
      section used to share a radio group, and a group only keeps one checked
      member, so the browser quietly cleared the rest. Theme scripts can also
      rewrite the picker during start-up. Either way the option heading still
      reads correctly while no swatch looks selected.
  
      This only syncs the DOM property to what the server already decided, so
      no change event fires and no variant state moves.
    */
    function ensurePreselection() {
      document.querySelectorAll('[data-option-fieldset]').forEach(function (fieldset) {
        var wanted = fieldset.getAttribute('data-selected-value');
        if (!wanted) return;
  
        var inputs = fieldset.querySelectorAll('input[type="radio"][data-option-position]');
        if (!inputs.length) return;
  
        var checked = false;
        var match = null;
        inputs.forEach(function (input) {
          if (input.checked) checked = true;
          if (!match && input.value === wanted) match = input;
        });
  
        /* Only step in when nothing at all is selected, so a customer's own
           choice is never overridden. */
        if (!checked && match) match.checked = true;
      });
    }
  
    function runPreselection() {
      ensurePreselection();
      /* main.js is a deferred module that initialises the pickers after this
         point, so check again once it has had its turn. */
      requestAnimationFrame(ensurePreselection);
      setTimeout(ensurePreselection, 300);
    }
  
    onReady(runPreselection);
    /* theme editor: a section is re-rendered and swapped in after a change */
    document.addEventListener('shopify:section:load', ensurePreselection);
  
    /* Keeps the "Colour: Red" text next to an option name in sync. */
    document.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || input.type !== 'radio') return;
      var position = input.getAttribute('data-option-position');
      if (!position) return;
      var fieldset = input.closest('[data-option-fieldset="' + position + '"]');
      if (!fieldset) return;
      var label = fieldset.querySelector('[data-selected-value-for="' + position + '"]');
      if (label) label.textContent = input.value;
    });
  })();

  /* ---- 2. Custom media gallery filter --------------------------------- */
  function startGalleryFilter() {
    (function () {
      if (window.__vpGalleryFilter) return;
      if (!document.querySelector('variant-picker[data-gallery-filter-config]')) return;
      window.__vpGalleryFilter = true;
    
      /*
        Custom media gallery filter.
    
        The theme's bundled script keeps only the run of media that belongs to
        the selected variant, which means shared / lifestyle images are dropped
        with no way to keep them. When the merchant asks for those to stay we
        set data-filter-gallery="false" so the bundled script restores the full
        gallery, and do the filtering here instead.
    
        Everything below is computed from a snapshot of the gallery taken
        before any filtering happens, so re-running it is always idempotent and
        it never depends on whatever the bundled script did first.
      */
    
      var CONFIG_SELECTOR = 'variant-picker[data-gallery-filter-config]';
      var SLIDER_SELECTOR = '.product-main-slider, .product-thumbs-slider, .product-thumbs-mobile-slider';
    
      var slideSnapshots = new WeakMap(); // carousel container -> original slides
      var gallerySnapshots = new WeakMap(); // gallery -> media grouping info
      var featuredSnapshots = new WeakMap(); // featured element -> original markup
      var gridSnapshots = new WeakMap(); // grid -> col-span layout flags
      var videoMarkup = new WeakMap(); // gallery -> video preview button markup
      var warmed = {}; // image urls already requested
    
      function readConfig() {
        var host = document.querySelector(CONFIG_SELECTOR);
        if (!host) return null;
        try {
          var parsed = JSON.parse(host.getAttribute('data-gallery-filter-config'));
          var always = String(parsed.always || '')
            .split(',')
            .map(function (item) { return parseInt(item, 10); })
            .filter(function (item) { return !isNaN(item) && item > 0; });
          var lastN = parseInt(parsed.lastN, 10);
          if (isNaN(lastN) || lastN < 0) lastN = 0;
          return {
            shared: parsed.shared || 'none',
            lastN: lastN,
            always: always,
            variantFirst: parsed.variantFirst === true
          };
        } catch (error) {
          return null;
        }
      }
    
      /*
        The theme marks only the first product image loading="eager"; the rest
        are lazy with fetchpriority="low". So the first time a variant is
        chosen its image has never been fetched, and it pops in a moment after
        the swap. Detaching and re-attaching a lazy <img> also resets its
        deferred state, so even an already-loaded one can blank briefly.
    
        Promoting the images we manage to eager fixes the second problem, and
        warming them in the background once the page is idle fixes the first.
      */
      function saveDataMode() {
        var connection = navigator.connection;
        if (!connection) return false;
        return Boolean(connection.saveData) || /(^|-)2g$/.test(connection.effectiveType || '');
      }
    
      /* Each <source> carries a single url, so the one the browser would pick
         can be resolved exactly rather than guessed. */
      function bestSource(img) {
        var picture = img.closest ? img.closest('picture') : null;
        if (picture) {
          var sources = picture.querySelectorAll('source[srcset]');
          for (var i = 0; i < sources.length; i++) {
            var media = sources[i].getAttribute('media');
            if (!media || window.matchMedia(media).matches) {
              return sources[i].getAttribute('srcset').split(',')[0].trim().split(' ')[0];
            }
          }
        }
        return img.currentSrc || img.getAttribute('src') || '';
      }
    
      function warmNode(node) {
        if (!node || !node.querySelectorAll) return;
        node.querySelectorAll('img').forEach(function (img) {
          if (img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
          if (img.getAttribute('fetchpriority') === 'low') img.removeAttribute('fetchpriority');
    
          if (img.complete && img.naturalWidth > 0) return;
          if (saveDataMode()) return;
    
          var url = bestSource(img);
          if (!url || warmed[url]) return;
          warmed[url] = true;
          var preload = new Image();
          preload.decoding = 'async';
          preload.src = url;
        });
      }
    
      /*
        Warms only the images that are a variant's featured image, including
        slides currently detached from the carousel.
    
        Those are the ones that land in front when a variant is picked, so they
        are what the customer actually watches load. Warming the whole gallery
        instead would pull down every image on every visit, which is wasted
        traffic for anyone who never opens the gallery. Any other media in a
        variant's group is still de-lazified at the moment it gets inserted,
        it just is not fetched up front.
      */
      function warmAll() {
        galleries().forEach(function (gallery) {
          var info = galleryInfo(gallery);
          if (!info) return;
    
          var consider = function (node) {
            var id = node && node.dataset ? node.dataset.mediaId : null;
            if (id && info.featured[id]) warmNode(node);
          };
    
          gallery.querySelectorAll('[data-media-id]').forEach(consider);
          gallery.querySelectorAll(SLIDER_SELECTOR).forEach(function (slider) {
            var container = slider.querySelector('.slider-carousel__container');
            if (!container) return;
            var original = slideSnapshots.get(container);
            if (original) original.forEach(consider);
          });
        });
      }
    
      function galleries() {
        return Array.prototype.slice.call(document.querySelectorAll('[data-product-gallery]'));
      }
    
      function staticItems(gallery) {
        return Array.prototype.slice.call(gallery.querySelectorAll('[data-media-id]')).filter(function (el) {
          return !el.closest('.slider-carousel') && !el.closest('slider-carousel');
        });
      }
    
      /* Snapshot the untouched gallery so filtering is always reversible. */
      function snapshot() {
        galleries().forEach(function (gallery) {
          gallery.querySelectorAll(SLIDER_SELECTOR).forEach(function (slider) {
            var container = slider.querySelector('.slider-carousel__container');
            if (!container || slideSnapshots.has(container)) return;
            slideSnapshots.set(
              container,
              Array.prototype.filter.call(container.children, function (el) {
                return !(
                  el.hasAttribute('data-clone') ||
                  el.hasAttribute('data-extra-repeat') ||
                  el.classList.contains('embla__slide--clone') ||
                  el.classList.contains('embla__slide--cloned')
                );
              })
            );
          });
    
          var videoButton = gallery.querySelector('[data-video-preview-button]');
          if (videoButton && !videoMarkup.has(gallery)) {
            videoMarkup.set(gallery, videoButton.outerHTML);
          }
    
          var grid = gallery.querySelector('.grid.grid-cols-2');
          if (grid && !gridSnapshots.has(grid)) {
            var items = Array.prototype.slice.call(grid.querySelectorAll('[data-media-id]'));
            gridSnapshots.set(grid, {
              lastLarge: items.length > 0 && items[items.length - 1].classList.contains('col-span-2'),
              everyFourth: items.some(function (el, index) {
                return (index + 1) % 4 === 0 && el.classList.contains('col-span-2');
              })
            });
            var featured = grid.previousElementSibling;
            if (featured && featured.dataset && featured.dataset.mediaId && !featuredSnapshots.has(featured)) {
              featuredSnapshots.set(featured, { html: featured.innerHTML, id: featured.dataset.mediaId });
            }
          }
        });
      }
    
      /*
        Rebuild the same media -> group map the theme uses: a group starts at
        every image assigned as a variant's featured image and runs until the
        next one. Also records which media sit before the first variant image
        ("leading"), after the last one ("trailing") and which are not any
        variant's featured image at all ("unassigned").
      */
      function galleryInfo(gallery) {
        var cached = gallerySnapshots.get(gallery);
        if (cached) return cached;
    
        var mediaRaw = gallery.getAttribute('data-media-json');
        var variantsRaw = gallery.getAttribute('data-variants-json');
        if (!mediaRaw || !variantsRaw) return null;
    
        var media, variants;
        try {
          media = JSON.parse(mediaRaw);
          variants = JSON.parse(variantsRaw);
        } catch (error) {
          return null;
        }
    
        var featured = {};
        variants.forEach(function (variant) {
          if (variant && variant.featured_media && variant.featured_media.id != null) {
            featured[String(variant.featured_media.id)] = true;
          }
        });
    
        var group = {};
        var order = [];
        var current = '';
        var first = -1;
        var last = -1;
    
        media.forEach(function (item, index) {
          var id = String(item.id);
          order.push(id);
          if (featured[id]) {
            current = id;
            if (first < 0) first = index;
            last = index;
          }
          if (current) group[id] = current;
        });
    
        var info = { order: order, featured: featured, group: group, first: first, last: last };
        gallerySnapshots.set(gallery, info);
        return info;
      }
    
      /* Media ids that should stay visible for the given variant. */
      function allowedIds(gallery, variant, config) {
        var info = galleryInfo(gallery);
        if (!info) return null;
    
        var currentGroup =
          variant && variant.featured_media && variant.featured_media.id != null
            ? String(variant.featured_media.id)
            : '';
        if (!currentGroup) return null; // variant has no image, show everything
    
        var mode = config.shared;
        var allowed = {};
        var count = 0;
    
        info.order.forEach(function (id, index) {
          var keep = info.group[id] === currentGroup;
    
          /* Media sitting before the first variant image belongs to no group
             at all, so the theme drops it from every variant. */
          if (!keep && mode === 'leading' && info.first >= 0 && index < info.first) {
            keep = true;
          }
          if (!keep && mode === 'unassigned' && !info.featured[id]) {
            keep = true;
          }
          /* Exact count, not a guess: once past the first variant image every
             remaining item belongs to some group, so there is no way to tell
             the last variant's own extra photos from shared ones. */
          if (!keep && config.lastN > 0 && index >= info.order.length - config.lastN) {
            keep = true;
          }
          if (!keep && config.always.indexOf(index + 1) > -1) {
            keep = true;
          }
    
          if (keep) {
            allowed[id] = true;
            count++;
          }
        });
    
        return count > 0 ? allowed : null;
      }
    
      function applyCarousels(gallery, allowed, activeId, config) {
        gallery.querySelectorAll(SLIDER_SELECTOR).forEach(function (slider) {
          var container = slider.querySelector('.slider-carousel__container');
          if (!container) return;
    
          var original = slideSnapshots.get(container);
          if (!original) return;
    
          var next = allowed
            ? original.filter(function (el) {
                var id = el.dataset.mediaId;
                return id && allowed[id];
              })
            : original.slice();
          if (!next.length) next = original.slice();
    
          /*
            Move the variant's own image to the front rather than scrolling the
            carousel along to wherever it happens to sit. Every slider in the
            gallery is reordered the same way, so main and thumbnail indexes
            stay in step.
          */
          var target = -1;
          if (activeId) {
            for (var i = 0; i < next.length; i++) {
              if (next[i].dataset.mediaId === activeId) {
                target = i;
                break;
              }
            }
          }
          if (config.variantFirst && target > 0) {
            next.unshift(next.splice(target, 1)[0]);
            target = 0;
          }
    
          var unchanged =
            next.length === container.children.length &&
            next.every(function (el, index) {
              return container.children[index] === el;
            });
    
          /*
            Only touch the DOM when the slide list genuinely differs. Scrolling
            lives in here too: re-running on an unchanged gallery must not yank
            the carousel away from wherever the customer has swiped to.
          */
          if (unchanged) return;
    
          /* Promote before insertion so a re-attached slide never falls back
             into lazy deferral and blank out. */
          next.forEach(warmNode);
    
          /* One atomic mutation rather than clear-then-append, which avoids a
             frame where the carousel has no slides in it at all. */
          if (typeof container.replaceChildren === 'function') {
            container.replaceChildren.apply(container, next);
          } else {
            container.innerHTML = '';
            next.forEach(function (el) {
              container.appendChild(el);
            });
          }
          slider.dataset.originalSlides = String(next.length);
    
          /* Mirrors the theme: keep a video play button on the leading slide. */
          if (slider.classList.contains('product-main-slider') && next.length > 0) {
            var markup = videoMarkup.get(gallery);
            if (markup && !next[0].querySelector('[data-video-preview-button]')) {
              next[0].insertAdjacentHTML('beforeend', markup);
            }
          }
    
          if (slider.carousel && typeof slider.carousel.reInit === 'function') {
            slider.carousel.reInit();
          }
          if (typeof slider.reapplyStyles === 'function') {
            slider.reapplyStyles();
          }
          slider.dispatchEvent(
            new CustomEvent('sliderCarousel:slidesUpdated', {
              detail: { originalSlides: next.length },
              bubbles: true
            })
          );
    
          /* jump = true, so this lands instantly instead of animating across. */
          if (target > -1 && slider.carousel && typeof slider.carousel.scrollTo === 'function') {
            slider.carousel.scrollTo(target, true);
          }
    
          /* Disable drag/hover affordances when a single slide is left. */
          var single = next.length <= 1;
          slider.style.pointerEvents = single ? 'none' : '';
          if (single) slider.dispatchEvent(new Event('pointerleave'));
        });
      }
    
      function reflowGrid(grid) {
        var flags = gridSnapshots.get(grid);
        if (!flags) return;
        var items = Array.prototype.slice.call(grid.querySelectorAll('[data-media-id]'));
        if (!items.length) return;
        var visible = items.filter(function (el) {
          return el.style.display !== 'none';
        });
        items.forEach(function (el) {
          el.classList.remove('col-span-2');
        });
        if (flags.everyFourth) {
          visible.forEach(function (el, index) {
            if ((index + 1) % 4 === 0) el.classList.add('col-span-2');
          });
        }
        if (flags.lastLarge && visible.length > 0) {
          visible[visible.length - 1].classList.add('col-span-2');
        }
      }
    
      function applyStatic(gallery, allowed, activeId) {
        var items = staticItems(gallery);
        if (!items.length) return;
    
        var grid = gallery.querySelector('.grid.grid-cols-2');
        var featured = grid ? grid.previousElementSibling : null;
        var featuredOriginal = featured ? featuredSnapshots.get(featured) : null;
        var scope = grid ? Array.prototype.slice.call(grid.querySelectorAll('[data-media-id]')) : items;
    
        if (featured && featuredOriginal) {
          /* Promote the variant's own image into the large slot above the grid. */
          var promoted = null;
          if (activeId && (!allowed || allowed[activeId])) {
            promoted =
              scope.filter(function (el) {
                return el.dataset.mediaId === activeId;
              })[0] || null;
          }
    
          if (promoted) {
            if (featured.dataset.mediaId !== activeId) {
              /* The markup is cloned into the featured slot, so make sure the
                 image behind it is already fetched before it appears. */
              warmNode(promoted);
              featured.innerHTML = promoted.innerHTML;
              featured.dataset.mediaId = activeId;
              warmNode(featured);
            }
          } else if (featured.dataset.mediaId !== featuredOriginal.id) {
            featured.innerHTML = featuredOriginal.html;
            featured.dataset.mediaId = featuredOriginal.id;
          }
          featured.style.display = '';
    
          scope.forEach(function (el) {
            var id = el.dataset.mediaId;
            var visible = !allowed || (id && allowed[id]);
            if (visible && promoted && el === promoted) visible = false;
            if (visible && !promoted && id && id === featured.dataset.mediaId) visible = false;
            el.style.display = visible ? '' : 'none';
          });
        } else {
          items.forEach(function (el) {
            var id = el.dataset.mediaId;
            el.style.display = !allowed || (id && allowed[id]) ? '' : 'none';
          });
        }
    
        if (grid) reflowGrid(grid);
      }
    
      function apply(variant) {
        var config = readConfig();
        if (!config) return;
        snapshot();
    
        var activeId =
          variant && variant.featured_media && variant.featured_media.id != null
            ? String(variant.featured_media.id)
            : '';
    
        galleries().forEach(function (gallery) {
          var allowed = allowedIds(gallery, variant, config);
          applyCarousels(gallery, allowed, activeId, config);
          applyStatic(gallery, allowed, activeId);
        });
      }
    
      var lastVariant = null;
    
      function schedule(variant) {
        if (variant) lastVariant = variant;
        if (!lastVariant) return;
    
        /*
          Run synchronously first. The bundled handler restores the full slide
          list on every variant change (it has to, since we told it not to
          filter), so if we deferred to a timeout the browser would paint the
          full gallery and then the filtered one - that is the flicker. Because
          our listener is registered after the bundled one, this runs later in
          the same event dispatch, and the browser never paints in between.
        */
        apply(lastVariant);
    
        /*
          Safety net for first load, where the bundled handler defers its own
          carousel work until the carousel reports ready and can undo us after
          the fact. Both passes are free when the gallery already matches.
        */
        requestAnimationFrame(function () {
          apply(lastVariant);
        });
        setTimeout(function () {
          apply(lastVariant);
        }, 300);
      }
    
      function init() {
        snapshot();
    
        /*
          Registered here rather than at parse time on purpose. main.js is a
          module, so it is deferred and has already registered its own
          variant:changed listener by DOMContentLoaded. Registering after it
          means our handler runs after its reset, in the same dispatch.
        */
        document.addEventListener('variant:changed', function (event) {
          if (!event.detail || !event.detail.variant) return;
          schedule(event.detail.variant);
        });
    
        /* Deliberately deferred to idle: warming during load would compete
           with the hero image for bandwidth and hurt LCP. */
        var warmLater = function () {
          snapshot();
          warmAll();
        };
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(warmLater, { timeout: 3000 });
        } else {
          setTimeout(warmLater, 1200);
        }
    
        var host = document.querySelector(CONFIG_SELECTOR);
        if (!host || !host.dataset.currentVariant) return;
        try {
          schedule(JSON.parse(host.dataset.currentVariant));
        } catch (error) {
          /* no current variant yet, the variant:changed listener will cover it */
        }
      }
    
      init();
    })();
  }
  onReady(startGalleryFilter);
  document.addEventListener('shopify:section:load', startGalleryFilter);
})();
